import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { Text, Row, Col, FlexView, Separator } from '@cuemath/comps';
import COLORS from 'constants/colors';
import { RADIO_COLORS } from 'constants/reviews';

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: auto auto auto;
  margin-left: 15px;
`;

const TextArea = styled.textarea`
  width: 500px;
  height: 100px;
  overflow-y: scroll;
  resize: none;
  cursor: auto;
`;

const GridItems = styled.div`
  font-size: 14px;
  text-align: left;
  margin-bottom: 10px;
  padding: 5px;
`;

const RowStyle = styled(Row)`
  padding-top: 20px;
  padding-bottom: 10px;
`;

const Label = styled.label`
  display: flex;
`;

const Input = styled.input`
  box-sizing: border-box;
  height: 16px;
  width: 16px;
  margin-top: 2px;
  border: 1px solid rgba(42, 42, 46, 0.3);
  background-color: #ffffff;
  :hover {
    box-shadow: 0 0 5px 0px orange inset;
  }
  :before {
    content: '';
    display: block;
    width: 62%;
    height: 62%;
    margin: 20% auto;
    border-radius: 50%;
  }
  :checked:before {
    background-color: ${({ optionValue }) =>
      optionValue ? `${COLORS[optionValue]}` : `transparent`};
  }
`;

const Oval = styled.div`
  box-sizing: border-box;
  height: 24px;
  width: 24px;
  border: 1px solid #2557e4;
  opacity: 0.7;
  border-radius: 50%;
  position: relative;
`;

const InfoDetails = styled(FlexView)`
  display: none;
  position: absolute;
  width: 337px;
  top: -20px;
  left: 57px;
  border-radius: 5px;
  background-color: #ffffff;
  border: 0.5px solid rgba(31, 66, 255, 0.6);
  box-shadow: 0 -2px 12px 0 rgba(51, 79, 110, 0.05), 0 2px 12px 0 rgba(51, 79, 110, 0.05);
  padding: 18px 12px;
  z-index: 1;
  background-color: ${COLORS.WHITE};
  transform: translateX(-24px);
  ::after {
    content: '';
    position: absolute;
    width: 0;
    height: 0;
    top: 30px;
    left: 9px;
    border: 6px solid transparent;
    border-right-color: white;
    border-bottom-color: white;
    transform-origin: 0 0;
    transform: rotate(135deg);
    box-shadow: 1px 1px 0px 0px rgba(31, 66, 255, 0.6);
  }
`;

const InfoWrapper = styled.div`
  position: relative;

  :hover {
    ${InfoDetails} {
      display: flex;
    }
  }
`;

const FlexWrapper = styled(FlexView)`
  display: flex;
  flex-direction: row;
`;

const RadioStyle = styled.span`
  margin-left: 5px;
`;

const ParaColumn = styled(Row)`
  flex-basis: 30%;
`;

const ToolText = styled(Text)`
  text-align: left;
  color: ${({ color }) => `${COLORS[color]}`};
  font-family: 'Open Sans';
  font-size: 16px;
  font-weight: 600;
  line-height: 22px;
`;

const ToolDesc = styled(Text)`
  color: ${COLORS.ABBEY};
  opacity: 0.9;
  font-size: 12px;
  line-height: 15px;
`;

const StatusColumn = styled(Row)`
  flex-basis: 30%;
`;

const CommentsColumn = styled(Row)`
  flex-basis: 40%;
`;

const QuestStyle = styled(Text)`
  padding-right: 8px;
  padding-left: 10px;
  margin-top: 2px;
`;

const ToolStyle = styled(Text)`
  text-align: center;
  color: #1e47d7;
  font-family: 'Open Sans';
  font-size: 16px;
  font-weight: 600;
  line-height: 22px;
`;

const FeedbackSection = props => {
  let {
    radioOptions,
    stateValue,
    handleOptionChange,
    remark,
    onRemarkChange,
    disabled,
  } = props;

  return (
    <div>
      {radioOptions &&
        Array.isArray(radioOptions) &&
        radioOptions.length &&
        radioOptions.map(optData => {
          const { id, options, label, admin_info_text: adminInfoText, type } = optData;

          return (
            <div key={`feedback-${id}`}>
              <RowStyle nowrap>
                <ParaColumn>
                  <FlexWrapper>
                    {id !== 's2_q1' && (
                      <InfoWrapper>
                        <InfoDetails>
                          {adminInfoText &&
                            Array.isArray(adminInfoText) &&
                            adminInfoText.length &&
                            adminInfoText.map(res => (
                              <div key={`fb-${res.id}`}>
                                <ToolText color={RADIO_COLORS[res.id]}>{res.id}</ToolText>
                                <ToolDesc>{res.value}</ToolDesc>
                              </div>
                            ))}
                        </InfoDetails>
                        <Oval>
                          <ToolStyle>{label.charAt()}</ToolStyle>
                        </Oval>
                      </InfoWrapper>
                    )}
                    <Col>
                      <QuestStyle weight="semibold">{label}</QuestStyle>
                    </Col>
                  </FlexWrapper>
                  <Separator height={10} transparent />
                </ParaColumn>
                <StatusColumn>
                  <GridContainer>
                    {options &&
                      options.map(opt => {
                        const { id: optionId, value: optionValue } = opt;

                        const color = RADIO_COLORS[optionValue.toUpperCase()];

                        const setColor = type !== 'radio' ? null : color;

                        return (
                          <GridItems key={`grid-${id}.${optionId}`}>
                            <Label key={`${id}.${optionId}`}>
                              <Input
                                type={type}
                                name={id}
                                key={id}
                                onChange={e => handleOptionChange(e, stateValue)}
                                value={optionId}
                                optionValue={setColor}
                                disabled={disabled}
                                checked={
                                  stateValue[id] && stateValue[id].option === optionId
                                }
                              />
                              <RadioStyle>{optionValue}</RadioStyle>
                            </Label>
                          </GridItems>
                        );
                      })}
                  </GridContainer>
                </StatusColumn>
                {label && (
                  <CommentsColumn>
                    <TextArea
                      value={
                        stateValue[id] && stateValue[id].comment && !remark[id]
                          ? stateValue[id].comment
                          : remark[id]
                      }
                      onChange={e => onRemarkChange(e, id)}
                      disabled={disabled}
                      key={!remark ? 'remark' : remark.id}
                      id={id}
                    />
                  </CommentsColumn>
                )}
              </RowStyle>
            </div>
          );
        })}
    </div>
  );
};

FeedbackSection.propTypes = {
  disabled: PropTypes.bool.isRequired,
  handleOptionChange: PropTypes.func.isRequired,
  radioOptions: PropTypes.array,
  remark: PropTypes.object,
  stateValue: PropTypes.object.isRequired,
  onRemarkChange: PropTypes.func,
};

FeedbackSection.defaultProps = {
  onRemarkChange: null,
  radioOptions: null,
  remark: null,
};

export default FeedbackSection;
